import React, { useState } from "react";
import AppLayout from "../components/AppLayout";
import { currentUser, notifications as initialNotifs } from "../data/mockData";
import { toast } from "sonner";

export default function Settings() {
    const [name, setName] = useState(currentUser.name);
    const [username, setUsername] = useState(currentUser.username);
    const [email, setEmail] = useState(currentUser.email);
    const [bio, setBio] = useState(currentUser.bio);
    const [notifs, setNotifs] = useState(initialNotifs);

    const Toggle = ({ value, onChange, testid }) => (
        <button
            type="button"
            data-testid={testid}
            onClick={() => onChange(!value)}
            className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-lime" : "bg-ink-600 border border-white/10"}`}
        >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
        </button>
    );

    return (
        <AppLayout searchPlaceholder="Search settings...">
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-8">Settings</h1>

            <div className="flex gap-8 border-b border-white/5 mb-8">
                {["Profile", "Notifications", "Privacy", "Account"].map((t, i) => (
                    <button
                        key={t}
                        data-testid={`settings-tab-${t.toLowerCase()}`}
                        className={`pb-3 text-sm font-semibold relative ${i === 0 ? "text-white" : "text-gray-500 hover:text-white transition-colors"}`}
                    >
                        {t}
                        {i === 0 && <span className="absolute -bottom-px left-0 w-full h-0.5 bg-lime rounded-full" />}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-ink-700 rounded-3xl border border-white/5 p-7">
                    <h2 className="font-display font-bold text-xl mb-6">Profile Information</h2>
                    <div className="flex items-center gap-5 mb-7">
                        <img src={currentUser.avatar} alt={currentUser.name} className="w-20 h-20 rounded-full object-cover" />
                        <div className="flex gap-3">
                            <button data-testid="upload-pic" onClick={() => toast.success("Picture upload simulated")} className="px-5 py-2.5 rounded-full bg-grape text-white text-sm font-bold hover:bg-grape-hover transition-colors">
                                Upload new picture
                            </button>
                            <button data-testid="delete-pic" className="text-sm text-gray-400 hover:text-white transition-colors">Delete</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-xs text-gray-400 mb-2">Full Name</label>
                            <input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-lime/40" />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-2">Username</label>
                            <input data-testid="input-username" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-lime/40" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-2">Email Address</label>
                            <input data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-lime/40" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-2">Your Bio</label>
                            <textarea data-testid="input-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={4} className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-lime/40 resize-none" />
                        </div>
                    </div>

                    <div className="mt-7 flex justify-end gap-3">
                        <button className="px-5 py-2.5 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors">Cancel</button>
                        <button data-testid="save-profile" onClick={() => toast.success("Profile saved")} className="px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                            Save changes
                        </button>
                    </div>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-6">
                        <h3 className="font-display font-bold text-lg mb-5">Notifications</h3>
                        <div className="space-y-4">
                            {[
                                { key: "emailNotifications", label: "Email Notifications" },
                                { key: "pushNotifications", label: "Push Notifications" },
                                { key: "monthlyReports", label: "Monthly Reports" },
                            ].map((n) => (
                                <div key={n.key} className="flex items-center justify-between">
                                    <span className="text-sm">{n.label}</span>
                                    <Toggle
                                        value={notifs[n.key]}
                                        onChange={(v) => setNotifs({ ...notifs, [n.key]: v })}
                                        testid={`toggle-${n.key}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-6">
                        <h3 className="font-display font-bold text-lg mb-5">Privacy</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm">Profile Visibility</span>
                                <span className="text-sm text-gray-400">Public</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm">Show activity status</span>
                                <Toggle value={true} onChange={() => {}} testid="toggle-activity" />
                            </div>
                        </div>
                        <button className="mt-5 w-full text-center text-sm font-bold text-lime hover:underline">Manage Privacy Settings</button>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
